import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";

// Removes a single variation from an image pack at (level, variation).
//
// Safety rule: every level must always contain at least one variation
// (otherwise the runtime's `Random.Range(0, pack[i].Length)` blows up).
// Consequences:
//   - Removing the last variation of a mid-array level is refused (would
//     create an empty level whose index can't be shifted without breaking
//     references throughout the data).
//   - Removing the last variation of the *trailing* level shrinks the
//     whole pack by one level (safe — no index shift).
//
// Returns true on successful mutation, false on no-op / refusal.
export class RemoveImageFromPackCommand extends CommandBase {
    packId: string;
    level: number;
    variation: number;

    constructor(packId: string, level: number, variation: number) {
        super();
        this.packId = packId;
        this.level = level;
        this.variation = variation;
    }

    execute(data: RemoveConstantsLike): boolean {
        const pack = data.imagePacks?.[this.packId];
        if (!pack || this.level < 0 || this.level >= pack.length) return false;

        const variations = pack[this.level];
        if (this.variation < 0 || this.variation >= variations.length) return false;

        if (variations.length === 1) {
            // Only the trailing level may collapse — anything else would
            // create an empty mid-array slot.
            if (this.level !== pack.length - 1) return false;
            pack.pop();
            return true;
        }

        variations.splice(this.variation, 1);
        return true;
    }
}

// Helper type alias so the test/migration code can pass the same shape.
type RemoveConstantsLike = RemoteConstantsModel;
